import React, { useEffect } from 'react';
import { PanelItem } from "@components/panels/PanelItems";
import useServiceHook from "@hooks/useServiceHook";
import PreviewInvoiceService, { TLineItem, TDiscountItem, CouponLabel, TAddon, IPreviewResponse } from '@services/previewInvoice/PreviewInvoiceService';
import LoadingSpinner from "@utils/loadingSpinner";
import InfoBox from "../../components/cards/messageBox/InfoBox";
import { countrySpecificCurrency, formatDateToDD_MMM_YYYY } from "@utils/misc/functions";
import { PricingRecordLineItem } from '@services/quotes/PricingRecords';
import { UserFormStates, useUserJourneyContext } from '@context/UserJourneyContext';


type InvoicePreviewProps = {
    country: string,
    hub: string,
    customerId: string,
    customerEmail: string,
    planId: string,
    planName: string,
    subscriptionStartDate: string,
    dynamic?: number,
    ignition?: number,
    ipb?: number,
    addons?: Array<any>
};


function DiscountLabel(label: string) {
    if (typeof label !== 'string')
        return ""
    else if (label.toLowerCase().includes("dynamic"))
        return "Dynamic Pricing"
    else if (label.toLowerCase().includes("ignition"))
        return "Ignition"
    else if (label.toLowerCase().includes("insurance"))
        return "Insurance Beat"
    else
        return label
}


function AddonLabel(label: string) {
    if (typeof label !== 'string')
        return ""
    else if (label.toLowerCase().includes("insurance"))
        return "Insurance"
    else
        return label
}


function infoExtraOnEndDate(dateToStr: string) {
    const dateEndOfWeek = new Date();  // Start with constructing today's date
    dateEndOfWeek.setDate(dateEndOfWeek.getDate() - dateEndOfWeek.getDay() + 7) // update to end of current week, Sunday

    const dateTo = new Date(dateToStr);  // Expecting YYYY-MM-DD

    if (dateTo > dateEndOfWeek)
        return " (up to the end of the second subscription week)"
    else
        return ""
}


export function InvoicePreview(props: InvoicePreviewProps) {

    const coupons = {
        ...(props.dynamic && { [CouponLabel.dynamic]: props.dynamic }),
        ...(props.ignition && { [CouponLabel.ignition]: props.ignition }),
        ...(props.ipb && { [CouponLabel.ipb]: props.ipb }),
    };

    const includedAddons: TAddon[] = props?.addons?.filter(
        (addon: PricingRecordLineItem) => addon.included == true
    ).map((addon: PricingRecordLineItem) => ({
        chargebeeItemType: addon.chargebee_item_type,
        chargebeeItemKey: addon.chargebee_item_key,
    })) || [];

    const [loading, error, errMsg, result] = useServiceHook(
        PreviewInvoiceService.getPreviewInvoice,
        [props.country, props.customerId, props.customerEmail, props.planId, props.subscriptionStartDate, coupons, includedAddons],
        true,
        [],
    ) as [boolean, boolean, string, IPreviewResponse];


    const { updateFormState } = useUserJourneyContext();

    const estimate = result?.estimate;

    useEffect(() => {
        if (result?.live_prices) {
            updateFormState(UserFormStates.cbpOnboard, { live_prices: result.live_prices });
        }
    }, [result?.live_prices, updateFormState]);

    const currencySymbol = countrySpecificCurrency(props.country);

    return (
        <>
            {loading && <LoadingSpinner testId={"loading-spinner"} />}
            {
                !loading && estimate && estimate["plan"] &&
                <>
                    <div style={{ margin: '2.5vh 0vw' }}>
                        <PanelItem header={`${props.hub} ${props.planName} - Prorated Charges`} headerTextStyleCfg={{ bold: true }}
                            value={`${currencySymbol}${estimate["plan"]["unit_amount"]}`} testId={'invoice-preview-plan-amount'} />
                        {
                            estimate["addon"]?.map((item: TLineItem) => (
                                <PanelItem key={item.id} header={(item.description || item.id).length > 50 ? 
                                    (item.description || item.id).replace(/ - /g, '\n') : 
                                    (item.description || item.id)} headerTextStyleCfg={{ bold: true }}
                                    testId={`invoice-preview-${item.id}`} value={`${currencySymbol}${item.unit_amount}`} />
                            ))
                        }   
                        {
                            estimate["discounts"]?.map((item: TDiscountItem) => (
                                <PanelItem key={item.id} header={`${DiscountLabel(item.id)}`} headerTextStyleCfg={{ bold: true }}
                                    testId={`invoice-preview-${item.id}`} value={`-${currencySymbol}${item.amount}`} />
                            ))
                        }
                        <div style={{ marginLeft: '50%', marginTop: '2vh' }}>
                            <PanelItem
                                header={`Total excl ${estimate.plan.tax_description}`}
                                headerTextStyleCfg={{ bold: true }}
                                value={`${currencySymbol}${estimate.subtotal_ex_tax}`}
                                testId="invoice-preview-plan-amount-exc-tax"
                            />
                            <PanelItem
                                header={`${estimate.plan.tax_description}`}
                                headerTextStyleCfg={{ bold: true }}
                                value={`${currencySymbol}${estimate.total_tax}`}
                                testId="invoice-preview-total-tax"
                            />
                            <PanelItem
                                header={`Credits`}
                                headerTextStyleCfg={{ bold: true }}
                                value={`${currencySymbol}${estimate.credits}`}
                                testId="invoice-preview-credits"
                            />
                            <PanelItem
                                header="Total amount due"
                                headerTextStyleCfg={{ bold: true }}
                                value={`${currencySymbol}${estimate.amount_due}`}
                                testId="invoice-preview-amount-due"
                            />
                        </div>
                    </div>
                    <div style={{ margin: '1.5vh 3.5vw' }}>
                        <InfoBox message={`Pro-rated charges apply to the period from ${formatDateToDD_MMM_YYYY(estimate["date_from"])} to ${formatDateToDD_MMM_YYYY(estimate["date_to"])}${infoExtraOnEndDate(estimate["date_to"])}`}
                            testId={'invoice-preview-info-box'} />
                    </div>
                </>
            }
        </>
    )
};
